/* eslint no-restricted-syntax: [
  "error",
  {
    "selector": "Literal[value='ui.data-table-preferences']",
    "message": "Use DATA_TABLE_PERSISTENCE_KEYS.localStorage.preferences."
  }
] */
import {
  type NotificationSeverity,
  NotificationSeverityLevel,
} from '@kirin/api-client/contracts/notification-settings';
import type { ThemePreferenceType } from '@/styles/types';
import type { TimestampFormat } from '@/contexts/TimestampFormatContext';
import type { ClockFormat, DateFormat } from '@/lib/absoluteTimeFormat';
import { DATA_TABLE_PERSISTENCE_KEYS } from '@/components/DataTable/types';

/** Last viewed profile (personal or org) for cross-tab / post-login restoration */
export type LastViewedProfile = {
  profileId: string;
  profileType: 'account' | 'organization';
} | null;

/**
 * Narrowed alert severity used for the last-saved severities record. `'none'`
 * is the off state itself and must never be persisted as the "last picked"
 * value.
 */
export type PersistedNotificationSeverity = Exclude<
  NotificationSeverity,
  NotificationSeverityLevel.None
>;

export type LocalStorageSchema = {
  'ui.theme-preference': ThemePreferenceType;
  'ui.timestamp-format': TimestampFormat;
  /** IANA zone for rendered date + time values; see lib/displayTimeZone. */
  'ui.time-zone': string;
  /** Absolute-timestamp date and clock style; see lib/absoluteTimeFormat. */
  'ui.date-format': DateFormat;
  'ui.clock-format': ClockFormat;
  'ui.sidebar-collapsed': boolean;
  'ui.ui-v2': boolean;
  'ui.ui-v2-collapsed-sections': string[];
  'ui.last-notification-severity': {
    personal: PersistedNotificationSeverity | null;
    orgs: Record<string, PersistedNotificationSeverity>;
  };
} & {
  [key: DataTablePreferencesKey]: {
    pagination: {
      pageIndex: number;
      pageSize: number;
    };
    filters: Array<{
      field: string;
      operator: string;
      value: string[];
    }>;
    sort?: {
      field: string;
      order: 'asc' | 'desc';
    } | null;
    search?: string;
  };
  lastViewedProfile: LastViewedProfile;
} & {
  [key: DataTablePreferencesKey]: {
    pagination: {
      pageIndex: number;
      pageSize: number;
    };
    filters: Array<{
      field: string;
      operator: string;
      value: string[];
    }>;
    sort?: {
      field: string;
      order: 'asc' | 'desc';
    } | null;
    search?: string;
  };
};

export type LocalStorageKey = keyof LocalStorageSchema;
export type LocalStorageValue<K extends LocalStorageKey = LocalStorageKey> =
  LocalStorageSchema[K];

export type DataTablePreferencesKey =
  `${typeof DATA_TABLE_PERSISTENCE_KEYS.localStorage.preferences}.${string}`;

export const dataTablePreferencesKey = (tableId: string) =>
  `${DATA_TABLE_PERSISTENCE_KEYS.localStorage.preferences}.${tableId}` as const;
